import type { CategoryQuery } from "@/widget-components/newsquawk-headlines/models/CategoryQuery";
import type { Portfolio } from "@/types/Portfolio";
import {
  isNotificationMethodEnabled,
  type NotificationMethod,
} from "@/types/notifications";
import { isEmptyView } from "./savedViewHighlight";

/**
 * Combined watchlists in the alert stores.
 *
 * The stores behind sound and pop-up alerts (HeadlinesFilterStore and the
 * firehose FilterStore) hold the numeric ids of the lists each widget filters
 * or highlights by, and the alert hooks resolve those ids against the
 * watchlists. Combined watchlists never went in, so their own alert settings
 * were never read — the combined builder offered sound and pop-up as "coming
 * soon".
 *
 * They now ride in the same id lists as negative ids, the convention
 * combinedWatchlistAsPortfolio already uses so the two id sequences cannot
 * collide. A watchlist lookup never finds a negative id, so everything that
 * only knows about watchlists behaves exactly as before.
 */

/** Alert-store ids for the combined watchlists selected in one section. */
export const combinedAlertIds = (selectedIds: string[] | undefined): number[] =>
  (selectedIds ?? [])
    .map((id) => Number(id))
    .filter((id) => Number.isInteger(id) && id > 0)
    .map((id) => -id);

/**
 * The combined watchlists among alert-store ids with `method` switched on.
 *
 * `usable` narrows further for a feed that can't evaluate every rule: the
 * firehose carries no tags, and a tags-only list there would match every
 * story.
 */
export function combinedAlertLists(
  ids: number[],
  combined: CategoryQuery[] | undefined,
  method: NotificationMethod,
  usable: (query: CategoryQuery) => boolean = () => true
): CategoryQuery[] {
  const wanted = new Set(ids.filter((id) => id < 0).map((id) => -id));
  if (wanted.size === 0) return [];
  return (combined ?? []).filter(
    (query) =>
      query.id != null &&
      wanted.has(query.id) &&
      isNotificationMethodEnabled(query.notifications, method) &&
      usable(query)
  );
}

/** A list an alert is about. */
export interface AlertMatch {
  /** `watchlist:<id>` or `combined:<id>`, as in highlightOrder.ts. */
  key: string;
  name: string;
}

/**
 * Every alerting list an item matches — watchlists, then combined watchlists.
 *
 * An alert isn't tied to one widget: one headline raises one alert for the
 * whole page, from the lists every widget has switched on. So no widget's
 * order can pick a winner here, and the alert names every list instead (see
 * alertTitle). An empty combined list never matches.
 */
export function matchingAlertLists<T>(
  item: T,
  watchlists: Portfolio[],
  combined: CategoryQuery[],
  matchWatchlist: (item: T, lists: Portfolio[]) => boolean,
  matchCombined: (item: T, query: CategoryQuery) => boolean
): AlertMatch[] {
  const matches: AlertMatch[] = [];
  const seen = new Set<string>();
  const add = (key: string, name: string) => {
    if (seen.has(key)) return;
    seen.add(key);
    matches.push({ key, name });
  };
  for (const list of watchlists) {
    if (matchWatchlist(item, [list])) add(`watchlist:${list.id}`, list.name);
  }
  for (const query of combined) {
    if (!isEmptyView(query) && matchCombined(item, query)) {
      add(`combined:${query.id}`, query.queryName);
    }
  }
  return matches;
}

/**
 * The title and tag of the alert for the lists an item matched: every list,
 * by name — "Oil · Energy names", and "+N" past two — or none without a match.
 *
 * A name appears once: a watchlist converted to a combined one keeps the same
 * name for both halves. The tag is what the browser replaces an earlier alert
 * by, so a later headline for the same lists takes the place of the last one.
 */
export function alertTitle(
  matches: AlertMatch[]
): { title: string; tag: string } | undefined {
  if (matches.length === 0) return undefined;
  const names = [...new Set(matches.map((m) => m.name))];
  const shown = names.slice(0, 2).join(" · ");
  return {
    title: names.length > 2 ? `${shown} +${names.length - 2}` : shown,
    tag: matches.map((m) => m.key).join("+"),
  };
}
